import { useCallback, useMemo } from 'react';
import { LocalLineItem } from '../line-items/hooks/useLocalLineItems';
import { AdjustmentSettings } from '@/types/adjustment-filters';

interface BaselineSnapshot {
  lineItems: LocalLineItem[];
  adjustmentSettings: AdjustmentSettings;
  finalStepFields: {
    special_instructions: string;
    payment_terms: string;
    validity_period: number;
  };
}

interface CurrentState {
  localLineItems: LocalLineItem[];
  adjustmentSettings: AdjustmentSettings;
  finalStepFields: {
    special_instructions: string;
    payment_terms: string;
    validity_period: number;
  };
}

interface ChangeDetectionResult {
  hasChanges: boolean;
  hasLineItemChanges: boolean;
  hasAdjustmentChanges: boolean;
  hasFinalStepChanges: boolean;
  summary: {
    addedItems: number;
    removedItems: number;
    modifiedItems: number;
    adjustmentChanges: string[];
    finalStepChanges: string[];
  };
}

export const useEstimateChangeDetection = () => {
  
  // Normalize number for comparison (handle floating point)
  const normalizeNumber = useCallback((num: number | null | undefined): number => {
    if (num === null || num === undefined) return 0;
    return Math.round(num * 100) / 100;
  }, []);

  // Compare two line item arrays
  const compareLineItems = useCallback((
    baseline: LocalLineItem[],
    current: LocalLineItem[]
  ): { hasChanges: boolean; added: number; removed: number; modified: number } => {
    const baselineIds = new Set(baseline.map(li => li.id));
    const currentMap = new Map(current.filter(li => !li._isDeleted).map(li => [li.id, li]));
    const currentNonNewIds = new Set(
      current.filter(li => !li._isNew && !li._isDeleted).map(li => li.id)
    );

    // Count additions (new items)
    const added = current.filter(li => li._isNew && !li._isDeleted).length;

    // Count deletions (existing items marked as deleted)
    const removed = current.filter(li => !li._isNew && li._isDeleted).length;

    // Count modifications
    let modified = 0;
    baseline.forEach(baseItem => {
      const currentItem = currentMap.get(baseItem.id);
      if (!currentItem) return;
      if (currentItem._isDeleted) return;

      // Check for modifications
      if (
        normalizeNumber(currentItem.quantity_in_base_units) !== normalizeNumber(baseItem.quantity_in_base_units) ||
        normalizeNumber(currentItem.unit_cost_snapshot) !== normalizeNumber(baseItem.unit_cost_snapshot) ||
        currentItem.description !== baseItem.description ||
        currentItem.manual_override !== baseItem.manual_override ||
        normalizeNumber(currentItem.manual_unit_cost) !== normalizeNumber(baseItem.manual_unit_cost)
      ) {
        modified++;
      }
    });

    return {
      hasChanges: added > 0 || removed > 0 || modified > 0,
      added,
      removed,
      modified
    };
  }, [normalizeNumber]);

  // Compare adjustment settings
  const compareAdjustments = useCallback((
    baseline: AdjustmentSettings,
    current: AdjustmentSettings
  ): { hasChanges: boolean; changes: string[] } => {
    const changes: string[] = [];

    // Compare filters
    const baselineFilters = JSON.stringify(baseline.filters || []);
    const currentFilters = JSON.stringify(current.filters || []);
    if (baselineFilters !== currentFilters) {
      changes.push('Markup filters changed');
    }

    // Compare discount
    const baselineDiscount = normalizeNumber(baseline.discountPercentage);
    const currentDiscount = normalizeNumber(current.discountPercentage);
    if (baselineDiscount !== currentDiscount) {
      changes.push(`Discount changed from ${baselineDiscount}% to ${currentDiscount}%`);
    }

    return {
      hasChanges: changes.length > 0,
      changes
    };
  }, [normalizeNumber]);

  // Compare final step fields
  const compareFinalStepFields = useCallback((
    baseline: BaselineSnapshot['finalStepFields'],
    current: CurrentState['finalStepFields']
  ): { hasChanges: boolean; changes: string[] } => {
    const changes: string[] = [];

    if ((baseline.special_instructions || '') !== (current.special_instructions || '')) {
      changes.push('Special instructions changed');
    }
    if ((baseline.payment_terms || '') !== (current.payment_terms || '')) {
      changes.push('Payment terms changed');
    }
    if (baseline.validity_period !== current.validity_period) {
      changes.push(`Validity period changed from ${baseline.validity_period} to ${current.validity_period} days`);
    }

    return {
      hasChanges: changes.length > 0,
      changes
    };
  }, []);

  // Main detection function
  const detectChanges = useCallback((
    baseline: BaselineSnapshot,
    current: CurrentState
  ): ChangeDetectionResult => {
    const lineItemComparison = compareLineItems(baseline.lineItems, current.localLineItems);
    const adjustmentComparison = compareAdjustments(baseline.adjustmentSettings, current.adjustmentSettings);
    const finalStepComparison = compareFinalStepFields(baseline.finalStepFields, current.finalStepFields);

    return {
      hasChanges: lineItemComparison.hasChanges || adjustmentComparison.hasChanges || finalStepComparison.hasChanges,
      hasLineItemChanges: lineItemComparison.hasChanges,
      hasAdjustmentChanges: adjustmentComparison.hasChanges,
      hasFinalStepChanges: finalStepComparison.hasChanges,
      summary: {
        addedItems: lineItemComparison.added,
        removedItems: lineItemComparison.removed,
        modifiedItems: lineItemComparison.modified,
        adjustmentChanges: adjustmentComparison.changes,
        finalStepChanges: finalStepComparison.changes,
      }
    };
  }, [compareLineItems, compareAdjustments, compareFinalStepFields]);

  return {
    detectChanges,
    compareLineItems,
    compareAdjustments,
    compareFinalStepFields,
  };
};
